import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material'
import { STATUSES } from '@iboxen/db-models/constants'
import { useTranslation } from 'react-i18next'
import { useMe } from '../../hooks/useMe'
import { getSettings } from '../../redux/actions'
import { addParcelStatus } from '../../redux/parcels/slice.parcels'
import { showProblem, showDashedResponseText } from './showResponseText'

const RETURN_STATUS = 'marked-for-return'
const REVERT_STATUS = 'in-box'

/**
 * Adding a status to a parcel: the options this person may add, the reason code the one status
 * that wants it takes, whether the consumer hears about it, and the write.
 *
 * It is the parcel page's `statuses` section and the parcels list's status cell — one form rather
 * than two, since both end at the same endpoint and the same three guards:
 *  - the form at all is the user's `parcelAddStatus` (no carrier half, so not `can`), or
 *    `revertCollectedToInBox`, which offers in-box on a collected parcel and nothing else:
 *    no flow, no settings, no notification checkbox
 *  - the notification checkbox is iBoxen-only, and so is `omitStatusEvent`, which is
 *    therefore what an iBoxen user sends unless they tick the box
 *  - a reason code belongs to the one status that offers it and is never sent with another
 *
 * Renders nothing when there is nothing this person may add, so a caller can place it unconditionally.
 */
export default function AddStatusForm({ parcel, onChanged }) {
  const { t } = useTranslation('parcels')
  const { t: transResponseMessage } = useTranslation('responseMessages')
  const dispatch = useDispatch()
  const { user, carrier, can } = useMe()
  const [generalSettings, setGeneralSettings] = useState(null)
  const [chosenName, setChosenName] = useState(undefined)
  const [extra, setExtra] = useState(undefined)
  // Unticked by default: telling the consumer is the deliberate act, so a status added
  // without a thought does not reach them
  const [sendStatusNotification, setSendStatusNotification] = useState(false)
  const [saving, setSaving] = useState(false)

  const canAddStatus = user.isAllowedTo('parcelAddStatus')
  const canRevert = user.isAllowedTo('revertCollectedToInBox')
  const canSkipNotification =
    canAddStatus && carrier.isIboxen && can('setStatusWithoutNotification')

  // iBoxen's own carrier sits outside the roles that have a flowchart of their own
  const role = STATUSES.VALID_FLOW_STATUS_TRANSITIONS_ROLES.includes(
    `iadmin/${carrier.username}`
  )
    ? `role|iadmin/${carrier.username}`
    : 'role|iadmin'
  const flowStatusModifier =
    STATUSES.VALID_FLOW_STATUS_TRANSITIONS_ROLES.includes(
      `${role.substring(5)}:${window.environment}`
    )
      ? window.environment
      : undefined

  const mayAddAnyStatus =
    carrier.isAllowedTo('addAnyStatus') || user.isAllowedTo('addAnyStatus')

  // A status with no onward steps has no entry in the flow at all. The flow
  // also reads the parcel's status history, which a parcel may lack.
  const appendableStatuses = useMemo(() => {
    if (!canAddStatus) {
      return canRevert && parcel.currentStatus === 'collected'
        ? [REVERT_STATUS]
        : []
    }
    if (mayAddAnyStatus) {
      return window.statusOptions.appendable[parcel.flow || 'delivery'] ?? []
    }
    return (
      STATUSES.getStatusTransitionFlow(
        { ...parcel, statuses: parcel.statuses ?? [] },
        role,
        undefined,
        flowStatusModifier
      )[parcel.currentStatus] ?? []
    )
  }, [
    canAddStatus,
    canRevert,
    mayAddAnyStatus,
    parcel,
    role,
    flowStatusModifier,
  ])

  // Whatever the user picked is only kept while it is still on offer; after a
  // reload moves the parcel on, the list starts again from its first entry.
  const statusName = appendableStatuses.includes(chosenName)
    ? chosenName
    : appendableStatuses[0]

  // only the reason-code select needs the settings
  useEffect(() => {
    if (!canAddStatus) return undefined
    let cancelled = false
    // a failed read comes back as {error} or nothing at all; the select then stays away
    dispatch(getSettings())
      .then((data) => {
        if (!cancelled) setGeneralSettings(data?.response?.general ?? null)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [dispatch, canAddStatus])

  const returnCodes = generalSettings?.alternateReturnData
  const showReturnCodes = statusName === RETURN_STATUS && returnCodes?.enabled

  const saveAddStatus = async () => {
    setSaving(true)
    try {
      const { payload } = await dispatch(
        addParcelStatus({
          parcelId: parcel.parcelId,
          _id: parcel._id,
          addStatus: {
            name: statusName,
            // a code belongs to the one status that offers it; never send it with another
            ...(statusName === RETURN_STATUS && extra && { data: { extra } }),
          },
          ...(canSkipNotification &&
            !sendStatusNotification && { omitStatusEvent: true }),
        })
      )
      // the server's messages are keyed with dashes where it wrote spaces
      showDashedResponseText(payload, transResponseMessage)
    } catch (e) {
      // addParcelStatus has already shown its own error when the request failed;
      // this is for anything thrown around it
      showProblem(transResponseMessage)
    } finally {
      setSaving(false)
      onChanged()
    }
  }

  if (!canAddStatus && appendableStatuses.length === 0) return null

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      justifyContent="center"
      useFlexGap
      flexWrap="wrap"
    >
      <TextField
        select
        size="small"
        label={t('addStatus')}
        value={statusName ?? ''}
        onChange={(e) => {
          setChosenName(e.target.value)
          if (e.target.value !== RETURN_STATUS) setExtra(undefined)
        }}
        sx={{ minWidth: 240 }}
      >
        {appendableStatuses.map((name) => (
          <MenuItem key={name} value={name}>
            {t(`statuses.${name}`)}
          </MenuItem>
        ))}
      </TextField>
      {/* Specialhaxx: Bring needs to attach extra data to this one status,
          and it stays a hack until that has a proper home */}
      {showReturnCodes && (
        <TextField
          select
          size="small"
          label={t('pickCodes')}
          value={extra ? JSON.stringify(extra) : ''}
          onChange={(e) =>
            e.target.value.includes('ReasonCode') &&
            setExtra(JSON.parse(e.target.value))
          }
          sx={{ minWidth: 240 }}
        >
          {(returnCodes.statuses ?? []).map((code) => (
            <MenuItem key={JSON.stringify(code)} value={JSON.stringify(code)}>
              {code.description}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Button
        variant="contained"
        color="iboxen"
        size="small"
        disabled={saving || !statusName}
        onClick={saveAddStatus}
      >
        {t('add')}
      </Button>
      {canSkipNotification && (
        <FormControlLabel
          control={
            <Checkbox
              checked={sendStatusNotification}
              onChange={(e) => setSendStatusNotification(e.target.checked)}
            />
          }
          label={t('sendStatusNotification')}
        />
      )}
    </Stack>
  )
}
